import { t } from "@/lib/i18n"

/**
 * Shape, endpoints and copy lookup for supplies, recipes, stock and waste.
 *
 * ## Three different sentences, and only the server knows which one is true
 *
 * A restaurant looking at an empty shortage list is in one of three situations,
 * and telling them apart is the whole reason `config/location_features.yml`
 * ships this feature switched off:
 *
 * 1. **The platform has not granted it.** Not a state rendered here at all —
 *    the nav hides the link and `Staff::Location::FeatureGuard` turns the URL
 *    away. Absence, not a greyed-out card.
 * 2. **There are no recipes yet.** Nothing draws stock down, so the balance can
 *    only ever go up and every shortage the screen could report is a data-entry
 *    mistake. `shortage.reportable` is false and **no shortage figure is
 *    rendered anywhere**, which is what `ShortageSummary` enforces.
 * 3. **They really are short.** The list, and only behind (2).
 *
 * `reportable` is a server answer on `location.recipe_lines.exists?`, never
 * re-derived from `coverage.covered > 0` — a restaurant whose only recipes hang
 * off extras still consumes stock, and calling that "no recipes" would hide real
 * movement.
 *
 * ## Nothing here computes a balance
 *
 * `short` and `belowReorderPoint` arrive answered, from `Location::Supply`'s own
 * predicates. `below_reorder_point?` has a nil branch — a blank reorder point
 * means "never warn me" — and reimplementing that against two decimal strings is
 * how the screen and the model end up disagreeing about a supply somebody is
 * standing in front of.
 *
 * ## Quantities are exact decimal strings and stay that way
 *
 * `decimal(12,3)` columns arrive as strings. They are rendered, not summed:
 * parsing a gram figure into a JavaScript Number to add it up is how 1234.567
 * becomes 1234.5669999999999 in a column a manager is checking against a shelf.
 * The one place a number is needed is the sign of a movement, and that reads the
 * leading character.
 */

export type SupplyUnit = "gram" | "kilogram" | "milliliter" | "liter" | "piece"

export type UnitOption = {
  value: SupplyUnit
  label: string
}

/**
 * The four ledger reasons a human may write. `purchase` and
 * `recipe_consumption` are absent on purpose: the system writes those two for
 * itself, and a ledger where "compra" sometimes means a delivery and sometimes
 * means somebody typing cannot answer what a kilo cost.
 */
export type AdjustmentReasonOption = {
  value: string
  label: string
  /** `theft` and `transfer_out`: the reason alone accuses or points nowhere. */
  noteRequired: boolean
  /** `count_correction`: the figure entered is the shelf, not the amount gone. */
  counted: boolean
}

export type StockMovement = {
  id: number
  reason: string
  reasonLabel: string
  /** Signed exact decimal string. Negative for anything that left. */
  quantity: string
  balanceAfter: string
  note: string | null
  createdAt: string
  /** Null when nothing human wrote it — a job, or a status change with no session. */
  actorName: string | null
  orderId: number | null
}

export type Supply = {
  id: number
  name: string
  unit: SupplyUnit
  unitLabel: string
  stockOnHand: string
  /** Null means "never warn me", not zero. */
  reorderPoint: string | null
  short: boolean
  belowReorderPoint: boolean
  /** A supply with movements cannot change unit: nothing in this feature converts. */
  unitLocked: boolean
  destroyable: boolean
}

export type SupplyDetail = Supply & {
  movements: StockMovement[]
}

export type ShortageState = {
  /** False until the restaurant has at least one recipe line anywhere. */
  reportable: boolean
  covered: number
  total: number
}

export type RecipeLine = {
  id: number
  supplyId: number
  supplyName: string
  unitLabel: string
  quantity: string
}

export type RecipeComponent = {
  id: number
  name: string
  status: string
  lines: RecipeLine[]
  /** Present on a dish. */
  categoryName?: string | null
  /** Present on an add-on. */
  groupName?: string | null
}

export type SupplyOption = {
  id: number
  name: string
  unit: SupplyUnit
  unitLabel: string
}

export const INVENTORY_PATHS = {
  supplies: "/manage/supplies",
  supply: (id: number) => `/manage/supplies/${id}`,
  adjustments: (supplyId: number) => `/manage/supplies/${supplyId}/adjustments`,
  recipes: "/manage/recipes",
  recipe: (id: number) => `/manage/recipes/${id}`,
  menu: "/manage/menu",
} as const

const I18N_PREFIX = "ui.react.features.staff.inventory"

export function tInventory(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/**
 * Whether a supply needs a manager's attention, and which sentence says so.
 *
 * `short` outranks `belowReorderPoint` because a negative balance is a fact
 * about the past — something was consumed that the system did not know it had —
 * while a reorder point is a request about the future. A supply that is both
 * gets the sentence about the one that already happened.
 */
export type SupplyAlert = "short" | "low" | null

export function supplyAlert(supply: Pick<Supply, "short" | "belowReorderPoint">): SupplyAlert {
  if (supply.short) return "short"
  if (supply.belowReorderPoint) return "low"
  return null
}

/**
 * The supplies a manager should look at, in the order they should look.
 *
 * Only ever called behind `shortage.reportable`. Calling it in front of a
 * restaurant with no recipes would produce a confident list of shortages that
 * are, every one of them, a typo in an opening count.
 */
export function suppliesNeedingAttention(supplies: Supply[]): Supply[] {
  return supplies.filter((supply) => supplyAlert(supply) !== null)
    .sort((a, b) => {
      const alertA = supplyAlert(a) === "short" ? 0 : 1
      const alertB = supplyAlert(b) === "short" ? 0 : 1
      return alertA - alertB || a.name.localeCompare(b.name)
    })
}

/**
 * A movement's direction, read off the string rather than parsed.
 *
 * The sign is the only thing about a quantity this screen needs to decide
 * anything, and `"-0.500"[0]` answers it without turning an exact decimal into a
 * float first.
 */
export function movementIsOutbound(quantity: string): boolean {
  return quantity.trimStart().startsWith("-")
}

/** `"-2.500"` reads as `2.500 kg` beside an arrow, not as a minus sign to squint at. */
export function movementMagnitude(quantity: string): string {
  const trimmed = quantity.trim()
  return trimmed.startsWith("-") || trimmed.startsWith("+") ? trimmed.slice(1) : trimmed
}

/**
 * Whether the adjustment dialog has everything its endpoint needs.
 *
 * The quantity check is deliberately loose — "is there anything typed" — because
 * `Location::StockAdjustmentService` is what decides whether a figure is a
 * number, whether it may be zero, and whether zero means "nothing changed". A
 * second copy of those rules in the browser is a copy that agrees on the day it
 * is written.
 */
export function adjustmentRequestComplete({
  quantity,
  note,
  reason,
  reasons,
}: {
  quantity: string
  note: string
  reason: string
  reasons: AdjustmentReasonOption[]
}): boolean {
  if (quantity.trim().length === 0) return false

  const option = reasons.find((candidate) => candidate.value === reason)
  if (!option) return false

  return !option.noteRequired || note.trim().length > 0
}
